import React, { createContext, Dispatch, PropsWithChildren, useEffect, useReducer } from 'react';
import store from 'store2';
import { IService, IServiceDetail, IXtimeMenuType, IXtimeServiceBase, MaintenanceMenu, RNDService } from '../API/GoMoto';
import { TransportSubtype, TransportType, QRCodeType } from '../API/Enums';
import { getUpdateAptRndServices, getUpdateAptServices } from '../helpers/Appointment';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentStateUnsafe } from './Appointment/appointment';
import { minEstCost as getCost, rndMinEstCost } from '../helpers/minEstCost';
import { ActionMap } from '../API/GoMoto.types';

// #region Context

export enum Types {
    Clear = 'CLEAR_CART',
    DisplayCartIcon = 'DISPLAY_CART_ICON',
    ClearSnackbar = 'CLEAR_SNACKBAR',

    AddService = 'ADD_SERVICE',
    RemoveService = 'REMOVE_SERVICE',
    UpdateService = 'UPDATESERVICE',
    ToggleService = 'TOGGLE_SERVICE',
    RemoveServiceSnack = 'REMOVE_SERVICE_SNACK',

    AddComment = 'ADD_COMMENT',
    RemoveComment = 'REMOVE_COMMENT',
    /** Updates an existing comment or adds one if it does not exist */
    UpdateComment = 'UPDATE_COMMENT',
    ToggleComment = 'TOGGLE_COMMENT',

    AddDetailItem = 'ADD_DETAIL_ITEM',
    RemoveDetailItem = 'REMOVE_DETAIL_ITEM',
    ToggleDetailItem = 'TOGGLE_DETAIL_ITEM',
    ClearDetailItems = 'CLEAR_DETAIL_ITEMS',

    AddXtimeMenu = 'ADD_XTIME_MENU',
    RemoveXtimeMenu = 'REMOVE_XTIME_MENU',
    ToggleXtimeMenu = 'TOGGLE_XTIME_MENU',
    RemoveXtimeMenuSnack = 'REMOVE_XTIME_MENU_SNACK',

    AddXtimeService = 'ADD_XTIME_SERVICE',
    RemoveXtimeService = 'REMOVE_XTIME_SERVICE',
    UpdateXtimeService = 'UPDATE_XTIME_SERVICE',
    ToggleXtimeService = 'TOGGLE_XTIME_SERVICE',
    RemoveXtimeServiceSnack = 'TOGGLE_XTIME_SERVICE_SNACK',

    AddMaintMenu = 'ADD_MAINT_MENU',
    RemoveMaintMenu = 'REMOVE_MAINT_MENU',
    ToggleMaintMenu = 'TOGGLE_MAINT_MENU',
    UpdateMaintMenu = 'UPDATE_MAINT_MENU',
    RemoveMenuSnack = 'REMOVE_MENU_SNACK',

    AddRNDService = 'ADD_RND_SERVICE',
    RemoveRNDService = 'REMOVE_RND_SERVICE',
    ToggleRNDService = 'TOGGLE_RND_SERVICE',
    RemoveRNDSnack = 'REMOVE_RND_SNACK',

    SetTransportation = 'ADD_TRANSPORTATION',
    SetTransportationComment = 'ADD_TRANSPORTATION_COMMENT',
    RemoveTransportationComment = 'REMOVE_TRANSPORTATION_COMMENT',

    SetCartMinEstimateCost = 'SET_CART_MIN_ESTIMATE_COST'
}

interface IXtimeCart {
    menus: IXtimeMenuType[];
    services: Array<IXtimeServiceBase>;
}

export type InitialStateType = {
    readonly displayCartIcon: boolean;
    readonly services: IService[];
    readonly comments: IComment[];
    readonly detailItems: IServiceDetail[];
    readonly xtime: IXtimeCart;
    readonly transportation: ITransport;
    readonly maintMenus: MaintenanceMenu[];
    readonly rndServices: RNDService[];
    readonly minEstCost: string;
};

const initialState: InitialStateType = {
    displayCartIcon: false,
    services: [],
    comments: [],
    detailItems: [],
    xtime: { menus: [],
        services: [] },
    transportation: { type: TransportType.drop },
    maintMenus: [],
    rndServices: [],
    minEstCost: '0.00',
};
type Actions = CartActions | ServiceActions | CommentActions | DetailItemActions | XtimeActions | TransportationActions | MaintMenuActions | RNDServiceActions;

const CartStateContext = createContext<InitialStateType>(initialState);
const CartDispatchContext = createContext<Dispatch<Actions>>(() => null);

export const CartProvider: React.FC<PropsWithChildren<{ useCache?: boolean; }>> = ({ children, useCache }) => {
    const cachedState = useCache ? ({ ...initialState,
        ...store('cart') } as InitialStateType) : initialState;
    const [state, dispatch] = useReducer(mainReducer, cachedState);
    const appointment = useAppointmentStateUnsafe();
    const appointmentDispatch = useAppointmentDispatch();

    useEffect(() => {
        if (useCache) {
            store('cart', state);
        }
    }, [state, useCache]);

    useEffect(() => {
        if (!appointment) {
            dispatch({ type: Types.Clear });
        }
    }, [appointment]);

    useEffect(() => {
        if (appointment && appointment.qrCodeType !== QRCodeType.vehicleReady) {
            const servicesConfirmed: typeof appointment.servicesConfirmed = [...getUpdateAptServices(state.services)];
            state.transportation.transportService && servicesConfirmed.push({ id: state.transportation.transportService.id });

            const allComments = state.comments.map((c) => c.comment);
            let comment = allComments.join('; ');
            if (comment.length === 0 && !!appointment.comment?.length) {
                comment = appointment.comment;
            }

            const hasDeletedServices =
                !appointment.servicesConfirmed || appointment.servicesConfirmed.findIndex((s) => servicesConfirmed.findIndex(({ id }) => s.id === id) === -1) > -1;
            const hasNewServices =
                !appointment.servicesConfirmed || servicesConfirmed.findIndex((s) => appointment.servicesConfirmed!.findIndex(({ id }) => s.id === id) === -1) > -1;

            const xtimeServices: typeof appointment.xtimeServices = [...state.xtime.services];
            const hasDeletedXtimeServices =
                !appointment.xtimeServices || appointment.xtimeServices.findIndex((s) => xtimeServices.findIndex(({ id }) => s.id === id) === -1) > -1;
            const hasNewXtimeServices =
                !appointment.xtimeServices || xtimeServices.findIndex((s) => appointment.xtimeServices!.findIndex(({ id }) => s.id === id) === -1) > -1;

            const maintenanceMenusConfirmed: typeof appointment.maintenanceMenusConfirmed = [...(state.maintMenus ?? [])];
            const hasDeletedMaintMenus =
                !appointment.maintenanceMenusConfirmed ||
                appointment.maintenanceMenusConfirmed.findIndex(
                    (m) => maintenanceMenusConfirmed.findIndex(({ id, scheduleOption }) => m.id === id && m.scheduleOption === scheduleOption) === -1,
                ) > -1;
            const hasNewMaintMenus =
                !appointment.maintenanceMenusConfirmed ||
                maintenanceMenusConfirmed.findIndex(
                    (m) => appointment.maintenanceMenusConfirmed!.findIndex(({ id, scheduleOption }) => m.id === id && m.scheduleOption === scheduleOption) === -1,
                ) > -1;

            const rndServices: RNDService[] = [...getUpdateAptRndServices(state.rndServices ?? [])];
            const hasDeletedRndServices =
                !appointment.rndServices || appointment.rndServices.findIndex((rnd) => rndServices.findIndex(({ opCode }) => rnd.opCode === opCode) === -1) > -1;
            const hasNewRndServices =
                !appointment.rndServices || rndServices.findIndex((rnd) => appointment.rndServices!.findIndex(({ opCode }) => rnd.opCode === opCode) === -1) > -1;

            if (
                hasNewServices ||
                hasDeletedServices ||
                hasDeletedXtimeServices ||
                hasNewXtimeServices ||
                appointment.comment !== comment ||
                hasNewMaintMenus ||
                hasDeletedMaintMenus ||
                hasNewRndServices ||
                hasDeletedRndServices
            ) {
                appointmentDispatch({
                    type: AppointmentDispatchTypes.Update,
                    payload: { ...appointment,
                        servicesConfirmed,
                        comment,
                        xtimeServices,
                        maintenanceMenusConfirmed,
                        rndServices },
                });
            }
        }
    }, [
        appointment,
        appointmentDispatch,
        state.comments,
        state.services,
        state.transportation.pickUpTime,
        state.transportation.transportService,
        state.xtime.services,
        state.maintMenus,
        state.rndServices,
    ]);

    useEffect(() => {
        const minEstCost = (
            (state.services.map((service) => getCost(service))
                .reduce((current, total) => (current ?? NaN) + (total ?? NaN), 0) ?? 0) +
            (state.maintMenus.map((menu) => getCost(menu))
                .reduce((current, total) => (current ?? NaN) + (total ?? NaN), 0) ?? 0) +
            (state.xtime.services.map((service) => service.price)
                .reduce((current, total) => (current ?? NaN) + (total ?? NaN), 0) ?? 0) +
            (state.xtime.menus.map((menu) => menu.totalPrice)
                .reduce((current, total) => (current ?? NaN) + (total ?? NaN), 0) ?? 0) +
            (state.rndServices.map((rnd) => (rnd.doneElsewhere ? 0 : rndMinEstCost(rnd)))
                .reduce((current, total) => (current ?? NaN) + (total ?? NaN), 0) ?? 0)
        ).toFixed(2);

        dispatch({ type: Types.SetCartMinEstimateCost,
            payload: minEstCost });
    }, [state.maintMenus, state.rndServices, state.services, state.xtime.menus, state.xtime.services]);

    return (
        <CartStateContext.Provider value={state}>
            <CartDispatchContext.Provider value={dispatch}>{children}</CartDispatchContext.Provider>
        </CartStateContext.Provider>
    );
};

export const useCart = () => React.useContext(CartStateContext);
export const useCartDispatch = () => React.useContext(CartDispatchContext);

// #endregion

// #region Reducers

const mainReducer = (state: InitialStateType, action: Actions): InitialStateType => {
    switch (action.type) {
        case Types.Clear:
        case Types.DisplayCartIcon:
        case Types.SetCartMinEstimateCost:
        case Types.ClearSnackbar:
            return cartReducer(state, action);
        default:
            return {
                ...state,
                services: serviceReducer(state.services, action as ServiceActions),
                comments: commentReducer(state.comments, action as CommentActions),
                detailItems: detailItemsReducer(state.detailItems, action as DetailItemActions),
                xtime: xtimeMenuReducer(state.xtime, action as XtimeActions),
                transportation: transportationReducer(state.transportation, action as TransportationActions),
                maintMenus: MaintMenuReducer(state.maintMenus, action as MaintMenuActions),
                rndServices: RNDServiceReducer(state.rndServices, action as RNDServiceActions),
            };
    }
};

type CartPayload = {
    [Types.Clear]: undefined;
    [Types.DisplayCartIcon]: typeof initialState.displayCartIcon;
    [Types.SetCartMinEstimateCost]: string;
    [Types.ClearSnackbar]: undefined;
};
type CartActions = ActionMap<CartPayload>[keyof ActionMap<CartPayload>];

const cartReducer = (state: InitialStateType, action: CartActions) => {
    switch (action.type) {
        case Types.Clear:
            return initialState;
        case Types.DisplayCartIcon:
            return { ...state,
                displayCartIcon: action.payload };
        case Types.SetCartMinEstimateCost:
            return { ...state,
                minEstCost: action.payload };
        case Types.ClearSnackbar:
            return {
                ...state,
                services: state.services.map((s) => {
                    s.snackable = false;

                    return s;
                }),
                maintMenus: state.maintMenus.map((s) => {
                    s.snackable = false;

                    return s;
                }),
                rndServices: state.rndServices.map((s) => {
                    s.snackable = false;

                    return s;
                }),
                xtime: {
                    ...state.xtime,
                    menu: state.xtime.menus.map((s) => {
                        s.snackable = false;

                        return s;
                    }),
                    services: state.xtime.services.map((s) => {
                        s.snackable = false;

                        return s;
                    }),
                },
            };
        default:
            return state;
    }
};

// #region Services

type ServicePayload = {
    [Types.AddService]: IService;
    [Types.UpdateService]: IService;
    [Types.ToggleService]: IService;
    [Types.RemoveService]: Pick<IService, 'id'>;
    [Types.RemoveServiceSnack]: number;
};

type ServiceActions = ActionMap<ServicePayload>[keyof ActionMap<ServicePayload>];

const serviceReducer = (state: IService[], action: ServiceActions) => {
    switch (action.type) {
        case Types.AddService:
            return [...state, action.payload];
        case Types.RemoveService:
            return [...state.filter((product) => product.id !== action.payload.id)];
        case Types.UpdateService: {
            const existing = state.findIndex((s) => s.id === action.payload.id);

            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing), action.payload];
            }
            else {
                return [...state];
            }
        }
        case Types.ToggleService: {
            const existing = state.findIndex((s) => s.id === action.payload.id);
            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing)];
            }
            else {
                return [...state.map((service) => ({ ...service,
                    ...{ snackable: false } })), { ...action.payload,
                    snackable: true }];
            }
        }
        case Types.RemoveServiceSnack:
            const existing = state.findIndex((s) => s.id === action.payload);
            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing), { ...state[existing],
                    snackable: false }];
            }
            else {
                return state;
            }
        default:
            return state;
    }
};
// #endregion

// #region Detail Items

type DetailItemsPayload = {
    [Types.AddDetailItem]: IServiceDetail;
    [Types.ToggleDetailItem]: IServiceDetail;
    [Types.RemoveDetailItem]: Pick<IServiceDetail, 'key'>;
    [Types.ClearDetailItems]: undefined;
};

type DetailItemActions = ActionMap<DetailItemsPayload>[keyof ActionMap<DetailItemsPayload>];

const detailItemsReducer = (state: IServiceDetail[], action: DetailItemActions) => {
    switch (action.type) {
        case Types.AddDetailItem:
            return [...state, action.payload];
        case Types.RemoveDetailItem:
            return [...state.filter((product) => product.key !== action.payload.key)];
        case Types.ToggleDetailItem:
            const existing = state.findIndex((s) => s.key === action.payload.key);
            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing)];
            }
            else {
                return [...state, action.payload];
            }
        case Types.ClearDetailItems:
            return initialState.detailItems;
        default:
            return state;
    }
};
// #endregion

// #region Comments
type CommentPayload = {
    [Types.AddComment]: IComment;
    [Types.RemoveComment]: IComment;
    [Types.UpdateComment]: IComment;
    [Types.ToggleComment]: IComment;
};

type CommentActions = ActionMap<CommentPayload>[keyof ActionMap<CommentPayload>];

export interface IComment {
    key: string;
    comment: string;
}

const commentReducer = (state: Array<IComment>, action: CommentActions) => {
    switch (action.type) {
        case Types.AddComment:
            return [...state, action.payload];
        case Types.RemoveComment:
            return [...state.filter((comment) => comment.key !== action.payload.key)];
        case Types.UpdateComment:
            return [...state.filter((comment) => comment.key !== action.payload.key), action.payload];
        case Types.ToggleComment:
            const existing = state.findIndex((comment) => comment.key === action.payload.key);
            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing)];
            }
            else {
                return [...state, action.payload];
            }
        default:
            return state;
    }
};

// #endregion

// #region Xtime Services

type XtimePayload = {
    [Types.AddXtimeMenu]: IXtimeMenuType;
    [Types.ToggleXtimeMenu]: IXtimeMenuType;
    [Types.RemoveXtimeMenu]: Pick<IXtimeMenuType, 'id'>;
    [Types.RemoveXtimeMenuSnack]: Pick<IXtimeMenuType, 'id'>;
    [Types.AddXtimeService]: IXtimeServiceBase;
    [Types.UpdateXtimeService]: IXtimeServiceBase;
    [Types.RemoveXtimeService]: Pick<IXtimeServiceBase, 'id' | 'type'>;
    [Types.ToggleXtimeService]: IXtimeServiceBase;
    [Types.RemoveXtimeServiceSnack]: number;
};

type XtimeActions = ActionMap<XtimePayload>[keyof ActionMap<XtimePayload>];

const xtimeMenuReducer = (state: IXtimeCart, action: XtimeActions) => {
    switch (action.type) {
        case Types.AddXtimeMenu:
            return addXtimeMenu(state, action.payload);
        case Types.RemoveXtimeMenu:
            return removeXtimeMenu(state, action.payload.id);
        case Types.ToggleXtimeMenu: {
            const existing = state.menus.findIndex((s) => s.id === action.payload.id);
            if (existing > -1) {
                return removeXtimeMenu(state, action.payload.id);
            }
            else {
                return addXtimeMenu(state, action.payload, true);
            }
        }
        case Types.RemoveXtimeMenuSnack: {
            const existing = state.menus.findIndex((s) => s.id === action.payload.id);
            if (existing > -1) {
                return { ...state,
                    menus: [...state.menus.filter((_, index) => index !== existing), { ...state.menus[existing],
                        snackable: false }] };
            }
            else {
                return state;
            }
        }
        case Types.AddXtimeService:
            return { ...state,
                services: [...state.services, action.payload] };
        case Types.RemoveXtimeService:
            return { ...state,
                services: [...state.services.filter((service) => service.type !== action.payload.type || service.id !== action.payload.id)] };
        case Types.UpdateXtimeService: {
            return {
                ...state,
                services: [...state.services.filter((service) => service.type !== action.payload.type || service.id !== action.payload.id), action.payload],
            };
        }
        case Types.ToggleXtimeService: {
            const existing = state.services.findIndex((s) => s.type === action.payload.type && s.id === action.payload.id);
            if (existing > -1) {
                return { ...state,
                    services: [...state.services.filter((_, index) => index !== existing)] };
            }
            else {
                return { ...state,
                    services: [...state.services.map((s) => ({ ...s,
                        ...{ snackable: false } })), { ...action.payload,
                        snackable: true }] };
            }
        }
        case Types.RemoveXtimeServiceSnack: {
            const existing = state.services.findIndex((s) => s.id === action.payload);
            if (existing > -1) {
                return { ...state,
                    services: [...state.services.filter((_, index) => index !== existing), { ...state.services[existing],
                        snackable: false }] };
            }
            else {
                return state;
            }
        }
        default:
            return state;
    }
};

const addXtimeMenu = (state: IXtimeCart, menu: IXtimeMenuType, withSnack: boolean = false) => {
    const menus = !withSnack ? [...state.menus, menu] : [...state.menus.map((m) => ({ ...m,
        ...{ snackable: false } })), { ...menu,
        snackable: true }];

    const services = [
        ...state.services,
        ...menu.services
            // When adding, we only want to add services the user cannot select, or selected by default.
            .filter((s) => !s.selectable || s.selectByDefault)
            .map(
                (service) =>
                    ({
                        id: service.id,
                        goMotoId: service.goMotoId,
                        type: 'menu',
                        menuId: menu.id,
                        opCode: service.dmsOpcode,
                        price: service.price,
                        isCommonRepair: false,
                        name: service.name,
                    } as IXtimeServiceBase),
            ),
    ];

    return { ...state,
        menus,
        services };
};

const removeXtimeMenu = (state: IXtimeCart, id: number) => {
    const menus = [...state.menus.filter((menu) => menu.id !== id)];
    const services = [...state.services.filter((service) => !service.menuId || service.menuId !== id)];

    return { ...state,
        menus,
        services };
};
// #endregion

// #region Transportation

export interface ITransport {
    transportService?: IService;
    type: TransportType;
    subtype?: TransportSubtype;
    pickUpTime?: string;
    dmsTransportationType?: string;
    opCode?: string;
}

type TransportationPayload = {
    [Types.SetTransportation]: Pick<ITransport, 'transportService' | 'type' | 'subtype' | 'dmsTransportationType' | 'opCode'>;
    [Types.SetTransportationComment]: Pick<ITransport, 'pickUpTime'>;
    [Types.RemoveTransportationComment]: undefined;
};

type TransportationActions = ActionMap<TransportationPayload>[keyof ActionMap<TransportationPayload>];

const transportationReducer = (state: ITransport, action: TransportationActions) => {
    switch (action.type) {
        case Types.SetTransportation:
        case Types.SetTransportationComment:
            return { ...state,
                ...action.payload };
        case Types.RemoveTransportationComment:
            return { ...state,
                pickUpTime: undefined,
                pickUpService: undefined };
        default:
            return state;
    }
};

// #endregion

// #region Maintenance Menus

type MaintMenuPayload = {
    [Types.AddMaintMenu]: MaintenanceMenu;
    [Types.RemoveMaintMenu]: Pick<MaintenanceMenu, 'id' | 'scheduleOption'>;
    [Types.ToggleMaintMenu]: MaintenanceMenu;
    [Types.UpdateMaintMenu]: MaintenanceMenu;
    [Types.RemoveMenuSnack]: Pick<MaintenanceMenu, 'id' | 'scheduleOption'>;
};

type MaintMenuActions = ActionMap<MaintMenuPayload>[keyof ActionMap<MaintMenuPayload>];

const MaintMenuReducer = (state: MaintenanceMenu[], action: MaintMenuActions) => {
    switch (action.type) {
        case Types.AddMaintMenu:
            return [...state, action.payload];
        case Types.RemoveMaintMenu:
            return [...state.filter((m) => !(m.id === action.payload.id && m.scheduleOption === action.payload.scheduleOption))];
        case Types.UpdateMaintMenu: {
            const existing = state.findIndex((m) => m.id === action.payload.id && m.scheduleOption === action.payload.scheduleOption);

            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing), action.payload];
            }
            else {
                return [...state];
            }
        }
        case Types.ToggleMaintMenu: {
            const existing = state.findIndex((m) => m.id === action.payload.id && m.scheduleOption === action.payload.scheduleOption);
            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing)];
            }
            else {
                return [...state.map((m) => ({ ...m,
                    ...{ snackable: false } })), { ...action.payload,
                    snackable: true }];
            }
        }
        case Types.RemoveMenuSnack:
            const existing = state.findIndex((m) => m.id === action.payload.id && m.scheduleOption === action.payload.scheduleOption);
            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing), { ...state[existing],
                    snackable: false }];
            }
            else {
                return state;
            }
        default:
            return state;
    }
};

// #endregion

// #region RND Services

type RNDServicePayload = {
    [Types.AddRNDService]: RNDService;
    [Types.RemoveRNDService]: Pick<RNDService, 'opCode'>;
    [Types.ToggleRNDService]: RNDService;
    [Types.RemoveRNDSnack]: Pick<RNDService, 'opCode'>;
};

type RNDServiceActions = ActionMap<RNDServicePayload>[keyof ActionMap<RNDServicePayload>];

const RNDServiceReducer = (state: RNDService[], action: RNDServiceActions) => {
    switch (action.type) {
        case Types.AddRNDService:
            return [...state, action.payload];
        case Types.RemoveRNDService:
            return [...state.filter((rnd) => !(rnd.opCode === action.payload.opCode))];
        case Types.ToggleRNDService: {
            const existing: number = state.findIndex((rnd) => rnd.opCode === action.payload.opCode);
            const remove: boolean = existing > -1 && state[existing].doneElsewhere === action.payload.doneElsewhere;

            // Toggle done elsewhere and selected
            if (existing > -1 && !remove) {
                return [
                    ...state.reduce((filteredServices: RNDService[], service: RNDService, index: number) => {
                        if (index !== existing) {
                            filteredServices.push({
                                ...service,
                                snackable: false,
                            });
                        }

                        return filteredServices;
                    }, []),
                    { ...action.payload,
                        snackable: !action.payload.doneElsewhere },
                ];
            }
            else if (existing === -1) {
                return [...state.map((rnd) => ({ ...rnd,
                    ...{ snackable: false } })), { ...action.payload,
                    snackable: !action.payload.doneElsewhere }];
            }
            else {
                return [...state.filter((_, index) => index !== existing)];
            }
        }
        case Types.RemoveRNDSnack:
            const existing = state.findIndex((rnd) => rnd.opCode === action.payload.opCode);
            if (existing > -1) {
                return [...state.filter((_, index) => index !== existing), { ...state[existing],
                    snackable: false }];
            }
            else {
                return state;
            }
        default:
            return state;
    }
};
// #endregion